import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X, MapPin, Store, Users, User, LogIn, Shield, Search, Sparkles, Heart, Crown, Palette, Wand2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/lib/authContext";
import { useRouteBuilder } from "@/contexts/RouteBuilderContext";
import { useRestyleVisibility, useShopOwnerFeatures } from "@/lib/featureFlags";
import { getLocalizedPath, useI18n } from "@/lib/i18n";
import { isAdminUser } from "@/lib/admin";

// Use logo from public folder
const logoUrl = "/logo.png";

export function Header() {
  const [location] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { tier, isLoggedIn, user } = useAuth();
  const { shops } = useRouteBuilder();
  const { canSeeRestyle } = useRestyleVisibility();
  const { canSeeShopOwner } = useShopOwnerFeatures();
  const { t, locale } = useI18n();
  const navItems = [
    { href: getLocalizedPath("routes", locale), label: t("nav.routes"), icon: MapPin },
    { href: getLocalizedPath("shops", locale), label: t("nav.shops"), icon: Store },
    { href: getLocalizedPath("wishlist", locale), label: t("nav.wishlist"), icon: Heart },
    { href: getLocalizedPath("community", locale), label: t("nav.community"), icon: Users },
    ...(canSeeRestyle ? [{ href: getLocalizedPath("restyle", locale), label: t("nav.restyle"), icon: Palette }] : []),
  ];

  const tierLabel = tier === "power" ? (
    <span className="flex items-center gap-1">
      <Crown className="w-3 h-3 text-yellow-500 fill-yellow-500" />
      Power User
    </span>
  ) : tier === "pro" ? "Insider" : tier === "free" ? t("header.tier.free") : null;
  const tierVariant = (tier === "pro" || tier === "power") ? "default" : "secondary";

  // Check if user is admin
  const isAdmin = isAdminUser(user);

  return (
    <header className="sticky top-0 z-50 w-full shadow-sm">
      <div className="w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
        <div className="container mx-auto flex h-16 items-center justify-between gap-4 px-4">
          <div className="flex-1 flex items-center">
            {shops.length > 0 && (
              <Link href="/routes/new">
                <Button
                  variant="default"
                  size="sm"
                  className="flex bg-primary text-white hover:bg-primary/90 font-bold animate-in fade-in slide-in-from-left-4 duration-300 h-9 px-2 sm:px-3 border-none shadow-sm"
                >
                  <MapPin className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
                  <span className="text-[11px] sm:text-xs md:text-sm">
                    <span className="hidden xs:inline">{t("header.planRoute")}</span>
                    <span className="xs:hidden">{t("header.planShort")}</span> ({shops.length})
                  </span>
                </Button>
              </Link>
            )}
          </div>

          {/* Logo and nav items centered */}
          <div className="flex items-center gap-6 flex-1 justify-center">
            <Link href="/" className="flex items-center gap-2 outline-none focus:outline-none focus-visible:outline-none" data-testid="link-home">
              <img
                src={logoUrl}
                alt="VintMap"
                className="h-[35px] sm:h-[40px] md:h-[45px] w-auto object-contain border-none"
                style={{ maxWidth: 'none' }}
              />
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map(({ href, label, icon: Icon }) => (
                <Link key={href} href={href}>
                  <Button
                    variant={location === href ? "secondary" : "ghost"}
                    size="sm"
                    className="gap-2"
                    data-testid={`link-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </Button>
                </Link>
              ))}
            </nav>
          </div>

          {/* Right side items */}
          <div className="flex items-center gap-2 flex-1 justify-end">
            {isAdmin && (
              <Link href="/backoffice">
                <Button variant="ghost" size="sm" className="hidden sm:inline-flex" data-testid="button-backoffice">
                  <Shield className="h-4 w-4 mr-2" />
                  {t("nav.admin")}
                </Button>
              </Link>
            )}

            {tierLabel && (
              <Link href="/account?tab=settings">
                <Badge variant={tierVariant} className="hidden sm:inline-flex cursor-pointer hover:opacity-80 transition-opacity" data-testid="badge-tier">
                  {tierLabel}
                </Badge>
              </Link>
            )}

            {/* Shop Owner Actions — hidden when shop_owner_features flag is off */}
            {canSeeShopOwner && (user?.role === 'owner' || (user?.claimedShopIds && Array.isArray(user.claimedShopIds) && user.claimedShopIds.length > 0)) && (
              <Link href="/account?tab=shop-admin">
                <Button variant="outline" size="sm" className="hidden sm:inline-flex border-primary text-primary hover:bg-primary/5" data-testid="button-my-shops">
                  <Store className="h-4 w-4 mr-2" />
                  {t("header.myShops")}
                </Button>
              </Link>
            )}

            {!isLoggedIn ? (
              <>
                <Link href="/login">
                  <Button variant="ghost" size="sm" data-testid="button-login">
                    {t("nav.login")}
                  </Button>
                </Link>
                <Link href="/login?signup=true">
                  <Button variant="default" size="sm" data-testid="button-signup">
                    {t("nav.signup")}
                  </Button>
                </Link>
              </>
            ) : (
              <Link href="/account">
                <Button variant="outline" size="icon" className="overflow-hidden rounded-full" data-testid="button-account">
                  {user?.avatarUrl ? (
                    <img src={user.avatarUrl} alt={user.username} className="h-full w-full object-cover" />
                  ) : (
                    <User className="h-4 w-4" />
                  )}
                </Button>
              </Link>
            )}

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="button-menu">
                  {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetTitle className="sr-only">{t("header.menu")}</SheetTitle>
                <SheetDescription className="sr-only">
                  {t("header.mobileMenuDesc")}
                </SheetDescription>
                <nav className="flex flex-col gap-2 mt-8">
                  {navItems.map(({ href, label, icon: Icon }) => (
                    <Link key={href} href={href} onClick={() => setMobileOpen(false)}>
                      <Button
                        variant={location === href ? "secondary" : "ghost"}
                        className="w-full justify-start gap-3"
                        data-testid={`link-mobile-${label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <Icon className="h-4 w-4" />
                        {label}
                      </Button>
                    </Link>
                  ))}
                  <div className="border-t border-border my-4" />
                  {isLoggedIn ? (
                    <Link href="/account" onClick={() => setMobileOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start gap-3" data-testid="link-mobile-account">
                        <User className="h-4 w-4" />
                        {t("nav.account")}
                        {tierLabel && (
                          <Badge variant={tierVariant} className="ml-auto cursor-pointer">
                            {tierLabel}
                          </Badge>
                        )}
                      </Button>
                    </Link>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <Link href="/login" onClick={() => setMobileOpen(false)}>
                        <Button
                          variant="secondary"
                          className="w-full"
                          data-testid="button-mobile-login"
                        >
                          <LogIn className="h-4 w-4 mr-2" />
                          {t("nav.login")}
                        </Button>
                      </Link>
                      <Link href="/login?signup=true" onClick={() => setMobileOpen(false)}>
                        <Button
                          variant="default"
                          className="w-full"
                          data-testid="button-mobile-signup"
                        >
                          {t("nav.signup")}
                        </Button>
                      </Link>
                    </div>
                  )}

                  {canSeeShopOwner && (user?.role === 'owner' || (user?.claimedShopIds && Array.isArray(user.claimedShopIds) && user.claimedShopIds.length > 0)) && (
                    <>
                      <div className="border-t border-border my-4" />
                      <Link href="/account?tab=shop-admin" onClick={() => setMobileOpen(false)}>
                        <Button variant="outline" className="w-full justify-start gap-3 border-primary text-primary bg-primary/5" data-testid="link-mobile-my-shops">
                          <Store className="h-4 w-4" />
                          {t("header.myShops")}
                        </Button>
                      </Link>
                    </>
                  )}

                  {isAdmin && (
                    <>
                      <div className="border-t border-border my-4" />
                      <Link href="/backoffice" onClick={() => setMobileOpen(false)}>
                        <Button variant="ghost" className="w-full justify-start gap-3" data-testid="link-mobile-backoffice">
                          <Shield className="h-4 w-4" />
                          {t("nav.admin")}
                        </Button>
                      </Link>
                    </>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Mobile Icon Bar */}
      <div className="lg:hidden w-full bg-primary border-t border-primary/20">
        <div className="flex items-center justify-around h-12">
          {navItems.map(({ href, icon: Icon, label }) => (
            <Link key={href} href={href} className="flex-1 h-full">
              <div className={`flex items-center justify-center h-full w-full transition-colors ${location === href ? 'bg-black/10' : 'hover:bg-black/5'}`}>
                <Icon className="h-5 w-5 text-white" />
                <span className="sr-only">{label}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </header>
  );
}
